import { Link, router } from "@inertiajs/react"
import { LogOut, Settings } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { UserInfo } from "@/components/user-info"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useMobileNavigation } from "@/hooks/use-mobile-navigation"
import { sessionPath, settingsProfilePath } from "@/routes"

/**
 * UserMenuContent — dropdown body shared by `<HeaderNavUser>` and `<NavUser>`.
 *
 * Sources `currentUser` + `currentSession` from the OKS-5 shared-props
 * contract via `useCurrentContext()`. Renders nothing when `currentUser` is
 * null (defensive — every menu host already null-guards).
 *
 * Sign-out targets `DELETE /sessions/:id` with the active session id and
 * carries `data-testid="user-menu-sign-out"` so demo + e2e scenarios can
 * locate it without depending on i18n'd visible labels (OKS-258).
 */
export function UserMenuContent() {
  const { currentUser, currentSession } = useCurrentContext()
  const { t } = useTranslation()
  const cleanup = useMobileNavigation()

  const handleLogout = () => {
    cleanup()
    router.flushAll()
  }

  if (!currentUser) return null

  return (
    <>
      <DropdownMenuLabel className="p-0 font-normal">
        <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
          <UserInfo user={currentUser} showEmail={true} />
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem
          render={
            <Link
              className="block w-full"
              href={settingsProfilePath()}
              as="button"
              prefetch
              onClick={cleanup}
              // OKS-799 — stable handle for the paused-creator route walk.
              // `creator_profile_controller.rb` includes
              // `CreatorPaused::RedirectConcern` while no controller under
              // `settings/` does, so for a PAUSED creator this link is the
              // ONLY route to the email-verification prompt. The E2E leg
              // walks the menu rather than `goto`-ing /settings/profile
              // precisely so that dropping this entry reds a test.
              //
              // Needs a testid rather than a selector: `as="button"` means
              // Inertia renders a `<button>` with no `href` to match on, and
              // selecting by accessible name would couple to `nav.settings`
              // — which renders FR by default (`default_locale = :fr`).
              // Mirrors `user-menu-sign-out` below.
              data-testid="user-menu-settings"
            >
              <Settings className="mr-2" />
              {t("nav.settings")}
            </Link>
          }
        />
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      {currentSession && (
        <DropdownMenuItem
          render={
            <Link
              className="block w-full"
              method="delete"
              href={sessionPath({ id: currentSession.id })}
              as="button"
              onClick={handleLogout}
              data-testid="user-menu-sign-out"
            >
              <LogOut className="mr-2" />
              {t("nav.sign_out")}
            </Link>
          }
        />
      )}
    </>
  )
}
